import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate } from 'react-router-dom';
import { CrmBadge, CrmButton, CrmSelect, CrmSwitch } from '../components/ui';
import { listCommerceEntity, updateCommerceEntity } from '../services/commercePolicyService';
import { getCommunicationBootstrap } from '../services/communicationService';
import { useSettings } from '../context/SettingsContext';
import '../assets/styles/Phase13Settings.scss';

const parseJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return fallback; }
};
const label = (value) => String(value || '—').replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
const errorText = (error) => error?.response?.data?.error || error?.message || 'Request failed.';
const releaseLabel = (value) => ({
  QUOTATION_APPROVAL: 'Quotation approved',
  PAYMENT_SUCCESS: 'Full payment received',
  ADVANCE_PAYMENT: 'Advance payment received',
  MANUAL_CONFIRMATION: 'Staff confirmation',
  AUTO_CONFIRM: 'Automatic confirmation',
  COD_ALLOWED: 'COD allowed',
}[String(value || '').toUpperCase()] || label(value));

const releaseEvent = (condition) => {
  const key = String(condition || '').toUpperCase();
  if (key === 'PAYMENT_SUCCESS') return 'PAYMENT_SUCCESS';
  if (key === 'QUOTATION_APPROVAL') return 'QUOTATION_APPROVED';
  if (key === 'ADVANCE_PAYMENT') return 'PAYMENT_SUCCESS';
  return 'ORDER_RELEASED';
};

export default function OperationalFlowSettings() {
  const navigate = useNavigate();
  const { commercialCatalog } = useSettings();
  const [profiles, setProfiles] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [metadata, setMetadata] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profileRows, communications] = await Promise.all([
        listCommerceEntity('fulfilmentProfiles'),
        getCommunicationBootstrap().catch(() => ({ rules: [] })),
      ]);
      const rows = Array.isArray(profileRows) ? profileRows : [];
      setProfiles(rows);
      setRules(communications?.rules || []);
      setSelectedId((current) => current && rows.some((row) => String(row.id) === String(current)) ? current : String(rows[0]?.id || ''));
    } catch (loadError) {
      setError(errorText(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const selected = useMemo(() => profiles.find((row) => String(row.id) === String(selectedId)) || null, [profiles, selectedId]);
  useEffect(() => {
    const current = parseJson(selected?.metadata, {});
    setMetadata({
      ...current,
      // orderLifecycleService treats an omitted flag as enabled for PAYMENT_SUCCESS.
      // Mirror that effective default here so the visual flow never lies about runtime behavior.
      create_invoice_on_release: current.create_invoice_on_release !== false,
      force_invoice_on_release: Boolean(current.force_invoice_on_release),
    });
  }, [selected]);

  const scopeName = useMemo(() => {
    if (!selected || String(selected.scope_type || 'GLOBAL').toUpperCase() === 'GLOBAL') return 'Global';
    const sourceByScope = {
      LEGAL_ENTITY: 'legal_entities',
      BRAND: 'brands',
      BUSINESS_UNIT: 'business_units',
      SALES_CHANNEL: 'sales_channels',
      STOREFRONT: 'storefronts',
    };
    const source = sourceByScope[String(selected.scope_type || '').toUpperCase()];
    const row = (commercialCatalog?.[source] || []).find((item) => Number(item.id) === Number(selected.scope_id));
    return row?.name || row?.legal_name || row?.trade_name || row?.code || `Record #${selected.scope_id}`;
  }, [commercialCatalog, selected]);

  const relevantTriggerKeys = useMemo(() => {
    if (!selected) return [];
    const entryKey = String(selected.entry_stage || '').toUpperCase() === 'QUOTATION' ? 'QUOTATION_CREATED' : 'ORDER_PLACED';
    return [...new Set([entryKey, releaseEvent(selected.release_condition), 'ORDER_RELEASED', metadata.create_invoice_on_release ? 'INVOICE_ISSUED' : null].filter(Boolean))];
  }, [metadata.create_invoice_on_release, selected]);

  const notificationRoutes = useMemo(() => (rules || []).filter((rule) => rule.is_enabled && relevantTriggerKeys.includes(rule.trigger_key)), [relevantTriggerKeys, rules]);

  const saveInvoiceAction = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const nextMetadata = {
        ...parseJson(selected.metadata, {}),
        ...metadata,
        create_invoice_on_release: Boolean(metadata.create_invoice_on_release),
        force_invoice_on_release: Boolean(metadata.force_invoice_on_release),
      };
      const saved = await updateCommerceEntity('fulfilmentProfiles', selected.id, { ...selected, metadata: nextMetadata });
      const normalized = saved?.id ? saved : { ...selected, metadata: nextMetadata };
      setProfiles((rows) => rows.map((row) => Number(row.id) === Number(selected.id) ? normalized : row));
      setMetadata(nextMetadata);
    } catch (saveError) {
      setError(errorText(saveError));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="phase13-loading">Loading operational flows…</div>;

  return (
    <div className="phase13-shell">
      {error ? <div className="phase13-error" role="alert">{error}</div> : null}
      <div className="phase13-toolbar">
        <div className="phase13-toolbar__filters">
          <CrmSelect label="Operational flow / Fulfilment Profile" value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
            <option value="">Select a profile</option>
            {profiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.name || profile.code || `Profile #${profile.id}`}</option>)}
          </CrmSelect>
        </div>
        <CrmButton variant="secondary" startIcon={<RefreshIcon />} onClick={load}>Refresh</CrmButton>
      </div>

      {selected ? (
        <>
          <div className="phase13-card">
            <div className="phase13-flow__header"><div><h3>{selected.name}</h3><p>{label(selected.scope_type || 'GLOBAL')} · {scopeName} · {selected.is_active === false || selected.is_active === 0 ? 'Inactive' : 'Active'}</p></div><CrmBadge tone={selected.is_active === false || selected.is_active === 0 ? 'neutral' : 'success'}>{selected.is_active === false || selected.is_active === 0 ? 'Inactive' : 'Active'}</CrmBadge></div>
            <div className="phase13-flow">
              <div className="phase13-flow__canvas">
                <div className="phase13-flow__node phase13-flow__node--event"><h4>{label(selected.entry_stage || 'ORDER')} enters JDH</h4><p>{selected.entry_stage === 'QUOTATION' ? 'Quotation becomes the commercial starting record.' : selected.entry_stage === 'DIRECT' ? 'Staff directly initiates the order flow.' : 'Unified Order is the starting record.'}</p><div className="phase13-flow__actions"><span className="phase13-flow__pill">Applies to: {scopeName}</span>{selected.requires_event_details ? <span className="phase13-flow__pill">Event details required</span> : null}</div></div>
                <div className="phase13-flow__connector"><ArrowForwardIcon /></div>
                <div className="phase13-flow__node phase13-flow__node--condition"><h4>{releaseLabel(selected.release_condition || 'MANUAL_CONFIRMATION')}</h4><p>Operations remain unreleased until this condition is satisfied under the existing Order Lifecycle rules.</p><div className="phase13-flow__actions">{selected.allow_manual_release ? <span className="phase13-flow__pill">Manual staff release allowed</span> : <span className="phase13-flow__pill">No manual override</span>}</div></div>
                <div className="phase13-flow__connector"><ArrowForwardIcon /></div>
                <div className="phase13-flow__node phase13-flow__node--action"><h4>Release actions</h4><p>The unified Order remains the master record. Release can create the kitchen KOT and begin physical fulfilment.</p><div className="phase13-flow__actions">{selected.requires_kot ? <span className="phase13-flow__pill">Create KOT</span> : null}{String(selected.fulfilment_requirement || '').toUpperCase() !== 'NOT_USED' ? <span className="phase13-flow__pill">{label(selected.fulfilment_requirement)} physical fulfilment</span> : null}{metadata.create_invoice_on_release ? <span className="phase13-flow__pill">Generate Tax Invoice</span> : null}{!selected.requires_kot && String(selected.fulfilment_requirement || '').toUpperCase() === 'NOT_USED' && !metadata.create_invoice_on_release ? <span className="phase13-flow__pill">No automatic artifacts</span> : null}</div></div>
                <div className="phase13-flow__connector"><ArrowForwardIcon /></div>
                <div className="phase13-flow__node phase13-flow__node--notification"><h4>Communications</h4><p>Notification routes resolving for events in this flow.</p><div className="phase13-flow__actions">{notificationRoutes.length ? notificationRoutes.slice(0, 5).map((rule) => <span className="phase13-flow__pill" key={rule.id}>{rule.trigger_key}: {rule.channel} → {rule.audience_type}</span>) : <span className="phase13-flow__pill">No new-engine routes configured</span>}{notificationRoutes.length > 5 ? <span className="phase13-flow__pill">+{notificationRoutes.length - 5} more routes</span> : null}</div></div>
              </div>
            </div>
          </div>

          <div className="phase13-grid">
            <div className="phase13-card">
              <div className="phase13-card__header"><div><h3>Invoice action</h3><p>Invoice generation is an explicit action of this operational profile.</p></div></div>
              <CrmSwitch label="Generate Tax Invoice on release" helperText="The invoice is generated from the unified Order when the profile's release condition allows it." checked={Boolean(metadata.create_invoice_on_release)} onChange={(event) => setMetadata((current) => ({ ...current, create_invoice_on_release: event.target.checked, force_invoice_on_release: event.target.checked ? current.force_invoice_on_release : false }))} />
              <CrmSwitch label="Force invoice on non-payment release" helperText="Advanced. Allows invoice generation on release conditions other than PAYMENT_SUCCESS." checked={Boolean(metadata.force_invoice_on_release)} disabled={!metadata.create_invoice_on_release} onChange={(event) => setMetadata((current) => ({ ...current, force_invoice_on_release: event.target.checked }))} />
              <div className="phase13-inline-actions"><span /><CrmButton loading={saving} startIcon={<SaveOutlinedIcon />} onClick={saveInvoiceAction}>Save invoice action</CrmButton></div>
            </div>
            <div className="phase13-card">
              <div className="phase13-card__header"><div><h3>Notification actions</h3><p>Routes are configured centrally so the flow does not duplicate provider/template logic.</p></div></div>
              <dl className="phase13-kv"><dt>Relevant triggers</dt><dd>{relevantTriggerKeys.join(', ') || '—'}</dd><dt>Enabled routes</dt><dd>{notificationRoutes.length}</dd></dl>
              <CrmButton variant="secondary" startIcon={<SettingsOutlinedIcon />} onClick={() => navigate('/settings?section=communications')}>Open Communications</CrmButton>
            </div>
          </div>

          <div className="phase13-card phase13-card--muted">
            <h3>Lifecycle source of truth</h3>
            <p>Flow origin, release condition, KOT requirement, physical fulfilment and staff override stay in Fulfilment Profiles. The visualizer deliberately reuses those existing fields rather than introducing a second workflow table.</p>
            <CrmButton variant="secondary" onClick={() => navigate('/settings?section=fulfilmentProfiles')}>Edit full Fulfilment Profile</CrmButton>
          </div>
        </>
      ) : <div className="phase13-empty">No Fulfilment Profile selected.</div>}
    </div>
  );
}
